import { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Small "?" hint that reveals an explanatory tooltip — used beside analytics-card
 * titles to explain what a metric means. Self-contained (bundles its own
 * TooltipProvider) so it can drop in anywhere.
 *
 * ⚠️ Touch support is the whole point of the controlled `open` state. A bare
 * Radix Tooltip opens on hover/focus ONLY — by design it is not a touch
 * primitive — so on a phone a tap did nothing (36 rage-clicks / 24 people over
 * 30 days on `/tender/*`, TZ 2026-08-30). Here a tap TOGGLES it and a tap outside
 * (or Escape) closes it, while Radix still handles the hard part — collision-aware
 * positioning + portalling above modals. Hover/focus keep working on desktop via
 * `onOpenChange`.
 *
 * ⚠️ A tap is NOT a bare click. On a real phone the order is pointer-down →
 * pointer-up → focus → click, and Radix opens the tooltip on that focus. A click
 * handler that simply flips the state therefore CLOSED the hint the focus had
 * just opened: it flashed for a fraction of a second and was gone (measured
 * 2026-10-13 on a Redmi 15C, Chrome 154, on both the lot page and the catalogue
 * count — every «?» on the site). The unit test fired `click` alone, which is why
 * it stayed green. So the click decides from the state AT PRESS TIME, not from
 * whatever focus did in between; a keyboard click (no press) still toggles.
 */
export function InfoHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** `open` when the finger (or mouse) went down; null between presses. */
  const openAtPressRef = useRef<boolean | null>(null);

  // Radix Tooltip closes itself on Escape and pointer-leave, but pointer-leave
  // never fires on touch, so an open-by-tap hint would stick. Close it on any
  // pointer-down that is not the trigger itself (whose onClick already toggles).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={text}
            aria-expanded={open}
            onPointerDown={() => {
              openAtPressRef.current = open;
            }}
            onClick={(e) => {
              // Ours is the only verdict on a click: Radix's own click handler
              // (close) is skipped for a default-prevented event.
              e.preventDefault();
              const openAtPress = openAtPressRef.current;
              openAtPressRef.current = null;
              setOpen(openAtPress === null ? (o) => !o : !openAtPress);
            }}
            // The GLYPH stays a 15 px circle; the tap target is the 24 px button
            // around it (WCAG 2.5.8 minimum), pulled in with a negative margin so
            // the line it sits on does not grow. Measured 2026-09-30: 15 × 15 on
            // the results count, unhittable on a phone.
            className="group -m-[4.5px] inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-[15px] w-[15px] items-center justify-center rounded-full border border-edge text-[10px] font-bold leading-none text-ink-muted transition-colors group-hover:border-brand group-hover:text-brand"
            >
              ?
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[240px] whitespace-normal font-sans text-[12px] normal-case leading-snug tracking-normal"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
